import type { MatrixQuarterlyReport } from "epsm-api-types/models";
import { useEffect, useState } from "react";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	formatValueBigNumber,
	formatValueDate,
	formatValueNumber,
	formatValuePercent,
} from "@/components/widgets/helpers/FormattingHelpers";
import { cn } from "@/lib/utils";
import {
	COMPONENT_CONTAINER_CLASSES,
	EMPTY_VALUE,
	forwardClassName,
	gradientClassName,
	MIN_MAX_COLUMN_CLASSES,
} from "../../../config";
import { coloredPercentTD } from "../../../lib/helpers";
import { MatrixReportField } from "../../../types";
import { TableWatermark } from "../TableWatermark";

const quarterLabels = ["Q-3", "Q-2", "Q-1"];
const selectOptions: { value: MatrixReportField; label: string }[] = [
	{ value: MatrixReportField.EPS, label: "EPS" },
	{ value: MatrixReportField.EPS_BNRI, label: "EPS(BNRI)" },
	{ value: MatrixReportField.SALES, label: "Sales" },
	{ value: MatrixReportField.PROFIT_MARGIN, label: "Profit Margin" },
	{ value: MatrixReportField.FREE_CASH_FLOW, label: "Free Cash Flow" },
];

const formatEM = (value?: number | null) => {
	if (value === null || value === undefined) return "";
	if (value < 0) return "text-error-light";
	if (value >= 20) return "text-success-light";
	if (value >= 0 && value < 20) return "text-light-golden-yellow";
	return "";
};

export const EarningMatrix = ({
	data,
	earningsMatrixViewMode,
	onStateChange,
}: {
	data: MatrixQuarterlyReport[];
	earningsMatrixViewMode: MatrixReportField;
	onStateChange?: OnStateChange;
}) => {
	const [estimateQuarterlyReports, setEstimateQuarterlyReports] = useState<
		MatrixQuarterlyReport[] | []
	>([]);
	const [quarterlyReports, setQuarterlyReports] = useState<
		MatrixQuarterlyReport[] | []
	>([]);
	const [activeField, setActiveField] = useState<MatrixReportField>(
		earningsMatrixViewMode,
	);

	useEffect(() => {
		const filteredQuarterlyReports = data?.filter(
			(report) => report?.earning_date,
		);
		const filteredEstimateQuarterlyReports = data?.filter(
			(report) => !report?.earning_date,
		);
		setQuarterlyReports(filteredQuarterlyReports);
		setEstimateQuarterlyReports(filteredEstimateQuarterlyReports);
	}, [data]);

	if (!data?.length) return false;

	return (
		<div className={COMPONENT_CONTAINER_CLASSES}>
			<h3 className="text-light-gray text-lg flex justify-between items-end">
				Earnings Matrix
				<span className="text-muted-foreground text-xs">
					NextEarnDate{" "}
					{formatValueDate(quarterlyReports[0]?.next_earning_date, EMPTY_VALUE)}
				</span>
			</h3>

			<table
				className="w-full text-right text-light-gray border-r-2 border-gray"
				data-testid="earning-matrix-table"
			>
				<colgroup>
					<col
						className={`font-normal text-center truncate ${MIN_MAX_COLUMN_CLASSES}`}
					/>
					<col
						className={`font-normal text-center ${MIN_MAX_COLUMN_CLASSES}`}
					/>
					<col
						className={`font-normal text-center ${MIN_MAX_COLUMN_CLASSES}`}
					/>
					<col
						className={`font-normal text-center ${MIN_MAX_COLUMN_CLASSES}`}
					/>
					<col
						className={`font-normal text-center ${MIN_MAX_COLUMN_CLASSES}`}
					/>
				</colgroup>
				<thead className="border-gray border-2">
					<tr className="border-gray border-2">
						<th>
							<Select
								onValueChange={(value) => {
									setActiveField(value as MatrixReportField);
									onStateChange?.({ earningsMatrixViewMode: value });
								}}
								value={activeField}
							>
								<SelectTrigger className="text-base text-gold">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{selectOptions.map(({ value, label }) => (
										<SelectItem key={value} value={value}>
											{label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</th>
						{quarterLabels.map((quarterLabel) => (
							<th key={quarterLabel}>{quarterLabel}</th>
						))}
						<th
							className={`font-normal text-center ${MIN_MAX_COLUMN_CLASSES} text-gold border-gray border-r-2 truncate`}
						>
							{formatValueDate(quarterlyReports[0]?.earning_date, EMPTY_VALUE)}
						</th>
					</tr>
				</thead>
				<tbody className="border-gray border-2">
					<tr>
						<td className="border-gray border-r-2 text-start truncate">Y-2</td>
						<td>
							{formatValueBigNumber(
								quarterlyReports[11]?.[activeField]?.value,
								EMPTY_VALUE,
							)}
						</td>
						<td>
							{formatValueBigNumber(
								quarterlyReports[10]?.[activeField]?.value,
								EMPTY_VALUE,
							)}
						</td>
						<td>
							{formatValueBigNumber(
								quarterlyReports[9]?.[activeField]?.value,
								EMPTY_VALUE,
							)}
						</td>
						<td className="border-gray border-r-2">
							{formatValueBigNumber(
								quarterlyReports[8]?.[activeField]?.value,
								EMPTY_VALUE,
							)}
						</td>
					</tr>
					<tr>
						<td className="border-gray border-r-2 text-start truncate">Y-1</td>
						<td>
							{formatValueBigNumber(
								quarterlyReports[7]?.[activeField]?.value,
								EMPTY_VALUE,
							)}
						</td>
						<td>
							{formatValueBigNumber(
								quarterlyReports[6]?.[activeField]?.value,
								EMPTY_VALUE,
							)}
						</td>
						<td>
							{formatValueBigNumber(
								quarterlyReports[5]?.[activeField]?.value,
								EMPTY_VALUE,
							)}
						</td>
						<td className="border-gray border-r-2">
							{formatValueBigNumber(
								quarterlyReports[4]?.[activeField]?.value,
								EMPTY_VALUE,
							)}
						</td>
					</tr>
					<tr>
						<td className="border-gray border-r-2 text-start truncate">Y</td>
						<td>
							{formatValueBigNumber(
								quarterlyReports[3]?.[activeField]?.value,
								EMPTY_VALUE,
							)}
						</td>
						<td>
							{formatValueBigNumber(
								quarterlyReports[2]?.[activeField]?.value,
								EMPTY_VALUE,
							)}
						</td>
						<td>
							{formatValueBigNumber(
								quarterlyReports[1]?.[activeField]?.value,
								EMPTY_VALUE,
							)}
						</td>
						<td
							className={cn(
								gradientClassName,
								"border-gray border-r-2 font-bold",
							)}
						>
							{formatValueBigNumber(
								quarterlyReports[0]?.[activeField]?.value,
								EMPTY_VALUE,
							)}
						</td>
					</tr>
					{!["free_cash_flow", "profit_margin"].includes(activeField) && (
						<tr className="border-gray border-b-2">
							<td
								className={cn(
									"border-gray border-r-2 text-start truncate w-fit",
									MIN_MAX_COLUMN_CLASSES,
								)}
							>
								<Tooltip>
									<TooltipTrigger
										asChild
										className={cn("truncate", MIN_MAX_COLUMN_CLASSES)}
									>
										<span className="truncate">
											Y+1
											<span className={cn(forwardClassName, "ml-1 truncate")}>
												Forward Est
											</span>
										</span>
									</TooltipTrigger>
									<TooltipContent>Y+1 Forward Est</TooltipContent>
								</Tooltip>
							</td>
							<td className={forwardClassName}>
								{formatValueBigNumber(
									estimateQuarterlyReports[0]?.[activeField]?.value,
									EMPTY_VALUE,
								)}
							</td>
							<td className={forwardClassName}>
								{formatValueBigNumber(
									estimateQuarterlyReports[1]?.[activeField]?.value,
									EMPTY_VALUE,
								)}
							</td>
							<td className={forwardClassName}>
								{formatValueBigNumber(
									estimateQuarterlyReports[2]?.[activeField]?.value,
									EMPTY_VALUE,
								)}
							</td>
							<td className={cn(forwardClassName, "border-gray border-r-2")}>
								{formatValueBigNumber(
									estimateQuarterlyReports[3]?.[activeField]?.value,
									EMPTY_VALUE,
								)}
							</td>
						</tr>
					)}
					<tr className="border-gray border-t-2">
						<td className="border-gray border-r-2 text-start truncate">
							Y-1 Chg%
						</td>
						{coloredPercentTD(quarterlyReports[7]?.[activeField]?.change)}
						{coloredPercentTD(quarterlyReports[6]?.[activeField]?.change)}
						{coloredPercentTD(quarterlyReports[5]?.[activeField]?.change)}
						{coloredPercentTD(
							quarterlyReports[4]?.[activeField]?.change,
							"border-gray border-r-2",
						)}
					</tr>
					<tr>
						<td className="border-gray border-r-2 text-start truncate">
							Y Chg%
						</td>
						{coloredPercentTD(quarterlyReports[3]?.[activeField]?.change)}
						{coloredPercentTD(quarterlyReports[2]?.[activeField]?.change)}
						{coloredPercentTD(quarterlyReports[1]?.[activeField]?.change)}
						{coloredPercentTD(
							quarterlyReports[0]?.[activeField]?.change,
							cn(gradientClassName, "border-gray border-r-2 font-bold"),
						)}
					</tr>
					{!["free_cash_flow", "profit_margin"].includes(activeField) && (
						<tr className="border-gray border-b-2">
							<td
								className={cn(
									"border-gray border-r-2 text-start truncate",
									MIN_MAX_COLUMN_CLASSES,
								)}
							>
								<Tooltip>
									<TooltipTrigger asChild>
										<span className="truncate">
											Y+1 Chg%
											<span
												className={cn(
													forwardClassName,
													"border-2 border-transparent truncate ml-1",
												)}
											>
												Forward Grth
											</span>
										</span>
									</TooltipTrigger>
									<TooltipContent>Y+1 Chg% Forward Grth</TooltipContent>
								</Tooltip>
							</td>
							<td className={forwardClassName}>
								{formatValuePercent(
									estimateQuarterlyReports[0]?.[activeField]?.change,
									EMPTY_VALUE,
								)}
							</td>
							<td className={forwardClassName}>
								{formatValuePercent(
									estimateQuarterlyReports[1]?.[activeField]?.change,
									EMPTY_VALUE,
								)}
							</td>
							<td className={forwardClassName}>
								{formatValuePercent(
									estimateQuarterlyReports[2]?.[activeField]?.change,
									EMPTY_VALUE,
								)}
							</td>
							<td className={cn(forwardClassName, "border-gray border-r-2")}>
								{formatValuePercent(
									estimateQuarterlyReports[3]?.[activeField]?.change,
									EMPTY_VALUE,
								)}
							</td>
						</tr>
					)}
					<tr className="border-gray border-t-2">
						<td className="border-gray border-r-2 text-start truncate">
							Y-1 Accel.
						</td>
						{coloredPercentTD(quarterlyReports[7]?.[activeField]?.acceleration)}
						{coloredPercentTD(quarterlyReports[6]?.[activeField]?.acceleration)}
						{coloredPercentTD(quarterlyReports[5]?.[activeField]?.acceleration)}
						{coloredPercentTD(
							quarterlyReports[4]?.[activeField]?.acceleration,
							"border-gray border-r-2",
						)}
					</tr>
					<tr>
						<td className="border-gray border-r-2 text-start truncate">
							Y Accel.
						</td>
						{coloredPercentTD(quarterlyReports[3]?.[activeField]?.acceleration)}
						{coloredPercentTD(quarterlyReports[2]?.[activeField]?.acceleration)}
						{coloredPercentTD(quarterlyReports[1]?.[activeField]?.acceleration)}
						{coloredPercentTD(
							quarterlyReports[0]?.[activeField]?.acceleration,
							cn(gradientClassName, "border-gray border-r-2 font-bold"),
						)}
					</tr>
					{!["free_cash_flow", "profit_margin"].includes(activeField) && (
						<tr className="border-gray border-b-2">
							<td
								className={cn(
									"border-gray border-r-2 text-start truncate",
									MIN_MAX_COLUMN_CLASSES,
								)}
							>
								<Tooltip>
									<TooltipTrigger asChild>
										<span className="truncate">
											Y+1 Accel.
											<span
												className={cn(
													forwardClassName,
													"border-2 border-transparent truncate ml-1",
												)}
											>
												Forward Acc
											</span>
										</span>
									</TooltipTrigger>
									<TooltipContent>Y+1 Accel. Forward Acc</TooltipContent>
								</Tooltip>
							</td>
							<td className={forwardClassName}>
								{formatValuePercent(
									estimateQuarterlyReports[0]?.[activeField]?.acceleration,
									EMPTY_VALUE,
								)}
							</td>
							<td className={forwardClassName}>
								{formatValuePercent(
									estimateQuarterlyReports[1]?.[activeField]?.acceleration,
									EMPTY_VALUE,
								)}
							</td>
							<td className={forwardClassName}>
								{formatValuePercent(
									estimateQuarterlyReports[2]?.[activeField]?.acceleration,
									EMPTY_VALUE,
								)}
							</td>

							<td className={cn(forwardClassName, "border-gray border-r-2")}>
								{formatValuePercent(
									estimateQuarterlyReports[3]?.[activeField]?.acceleration,
									EMPTY_VALUE,
								)}
							</td>
						</tr>
					)}
					<tr className="border-gray border-t-2">
						<td className="border-gray border-r-2 text-start truncate">
							Surprise
						</td>
						{coloredPercentTD(quarterlyReports[3]?.[activeField]?.surprise)}
						{coloredPercentTD(quarterlyReports[2]?.[activeField]?.surprise)}
						{coloredPercentTD(quarterlyReports[1]?.[activeField]?.surprise)}
						{coloredPercentTD(
							quarterlyReports[0]?.[activeField]?.surprise,
							"border-gray border-r-2",
						)}
					</tr>
					<tr className="border-gray border-b-2">
						<td className="border-gray border-r-2 text-start truncate">
							PriceChED
						</td>
						{coloredPercentTD(quarterlyReports[3]?.price_change_since_ed)}
						{coloredPercentTD(quarterlyReports[2]?.price_change_since_ed)}
						{coloredPercentTD(quarterlyReports[1]?.price_change_since_ed)}
						{coloredPercentTD(
							quarterlyReports[0]?.price_change_since_ed,
							"border-gray border-r-2",
						)}
					</tr>
					<tr className="border-gray border-b-2">
						<td className="border-gray border-r-2 text-start truncate">EM</td>
						<td className={formatEM(quarterlyReports[3]?.em?.value)}>
							{formatValueNumber(quarterlyReports[3]?.em?.value, EMPTY_VALUE)}
						</td>
						<td className={formatEM(quarterlyReports[2]?.em?.value)}>
							{formatValueNumber(quarterlyReports[2]?.em?.value, EMPTY_VALUE)}
						</td>
						<td className={formatEM(quarterlyReports[1]?.em?.value)}>
							{formatValueNumber(quarterlyReports[1]?.em?.value, EMPTY_VALUE)}
						</td>
						<td
							className={cn(
								"border-gray border-r-2",
								formatEM(quarterlyReports[0]?.em?.value),
							)}
						>
							{formatValueNumber(quarterlyReports[0]?.em?.value, EMPTY_VALUE)}
						</td>
					</tr>
				</tbody>
			</table>

			<TableWatermark />
		</div>
	);
};
